import React, { useState, useMemo, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import ProductCard from '@/components/shop/ProductCard';
import ShopFilters from '@/components/shop/ShopFilters';
import CompareBar from '@/components/shop/CompareBar';
import RecentlyViewed from '@/components/shop/RecentlyViewed';
import { deriveBrand } from '@/lib/productStore';
import { Loader2, Search, X, SlidersHorizontal } from 'lucide-react';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { usePageMedia } from '@/lib/usePageMedia';
import Testimonials from '@/components/Testimonials';

export default function Shop() {
  const { get } = usePageMedia();
  const heroImg = get('shop', 'hero');
  const [category, setCategory] = useState(
    () => new URLSearchParams(window.location.search).get('category') || 'all'
  );

  useSiteSeo(category === 'all' ? 'shop' : `category:${category}`, {
    title: 'Shop — Pro-Spec Sim Racing Hardware',
    description: 'Shop premium sim racing hardware: sim rigs, steering wheels, pedals, motion platforms, monitors, brackets and gaming computers. Next day UK delivery on orders before 1pm Mon–Thu.',
  });
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ brand: 'all', platforms: [], inStockOnly: false, minPrice: '', maxPrice: '' });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list('-created_date'),
  });

  const { data: allCategories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => base44.entities.Category.list('sort_order'),
  });

  // Deduplicate products into representative cards.
  // Manual products (no Square ID) are included as-is; Square products are grouped by square_item_id.
  const representatives = useMemo(() => {
    const groups = new Map();
    const manuals = [];
    for (const p of products) {
      if (p.shop_visible === false) continue;
      if (p.manual_product) {
        manuals.push(p);
        continue;
      }
      if (!p.square_item_id) continue;
      if (!groups.has(p.square_item_id)) groups.set(p.square_item_id, []);
      groups.get(p.square_item_id).push(p);
    }
    const reps = [];
    for (const group of groups.values()) {
      const variants = group.filter(p => p.is_variant);
      if (variants.length === 0) {
        reps.push(group[0]);
        continue;
      }
      const isBlack = (p) => {
        const s = `${p.variation_name || ''} ${p.option_value || ''} ${p.name || ''}`.toLowerCase();
        return s.includes('black');
      };
      reps.push(variants.find(isBlack) || variants[0]);
    }
    return [...reps, ...manuals];
  }, [products]);

  // Only show categories that are: active AND have at least one published, in-stock product
  const activeCategories = useMemo(() => {
    return allCategories.filter(cat => {
      if (!cat.active) return false;
      return representatives.some(p => {
        if (p.category !== cat.key) return false;
        if (p.published === false) return false;
        if (p.in_stock === false) return false;
        if (p.stock_quantity !== null && p.stock_quantity !== undefined && p.stock_quantity <= 0) return false;
        return true;
      });
    });
  }, [allCategories, representatives]);

  const availableBrands = useMemo(() => {
    const set = new Set();
    for (const p of representatives) {
      const b = deriveBrand(p);
      if (b) set.add(b);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [representatives]);

  const priceBounds = useMemo(() => {
    if (!representatives.length) return { min: 0, max: 0 };
    let min = Infinity, max = 0;
    for (const p of representatives) {
      const pr = Number(p.price);
      if (!isFinite(pr)) continue;
      if (pr < min) min = pr;
      if (pr > max) max = pr;
    }
    return { min: isFinite(min) ? min : 0, max };
  }, [representatives]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (p) => {
      if (!q) return true;
      return (
        (p.name?.toLowerCase().includes(q)) ||
        (p.variation_name?.toLowerCase().includes(q)) ||
        (p.sku?.toLowerCase().includes(q)) ||
        (p.description?.toLowerCase().includes(q))
      );
    };
    let list = representatives;
    if (category !== 'all') list = list.filter(p => p.category === category);
    if (q) list = list.filter(matches);
    if (filters.brand !== 'all') list = list.filter((p) => deriveBrand(p) === filters.brand);
    if (filters.platforms.length) {
      list = list.filter((p) => {
        const plat = p.platform || 'both';
        return filters.platforms.some((sel) => plat === sel || plat === 'both');
      });
    }
    if (filters.inStockOnly) {
      list = list.filter((p) => p.in_stock !== false && !(p.stock_quantity != null && p.stock_quantity <= 0));
    }
    const min = filters.minPrice !== '' ? Number(filters.minPrice) : null;
    const max = filters.maxPrice !== '' ? Number(filters.maxPrice) : null;
    if (min !== null || max !== null) {
      list = list.filter((p) => {
        const pr = Number(p.price);
        if (min !== null && pr < min) return false;
        if (max !== null && pr > max) return false;
        return true;
      });
    }
    return list;
  }, [representatives, category, search, filters]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.brand !== 'all') n++;
    if (filters.platforms.length) n++;
    if (filters.inStockOnly) n++;
    if (filters.minPrice !== '') n++;
    if (filters.maxPrice !== '') n++;
    return n;
  }, [filters]);

  // If selected category has been hidden, reset to all
  useEffect(() => {
    if (category !== 'all' && !activeCategories.find(c => c.key === category)) {
      setCategory('all');
    }
  }, [activeCategories, category]);

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="relative overflow-hidden py-20 px-6 lg:px-10 border-b border-white/5">
        {heroImg && (
          <>
            <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0f]/70 to-[#0d0d0f]" />
          </>
        )}
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">PRO-SPEC GEAR</p>
            <h1 className="text-white font-black text-4xl md:text-6xl uppercase leading-tight mb-3">
              THE SHOP
            </h1>
            <p className="text-white/40 text-sm max-w-lg">
              Premium simulation hardware engineered for serious racers. Next day UK delivery on orders before 1pm Mon–Thu.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter tabs */}
      <div className="border-b border-white/5 bg-[#0d0d0f] sticky top-[var(--header-offset)] z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sim racing rigs, wheels, pedals, accessories..."
              className="w-full bg-[#111115] border border-white/10 focus:border-primary/40 text-white text-sm pl-10 pr-10 py-3 outline-none transition-colors placeholder:text-white/30"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center overflow-x-auto gap-0 scrollbar-hide">
          <button
            onClick={() => setCategory('all')}
            className={`shrink-0 text-xs font-bold tracking-wider px-5 py-4 border-b-2 transition-all whitespace-nowrap ${
              category === 'all' ? 'border-primary text-primary' : 'border-transparent text-white/30 hover:text-white/60'
            }`}
          >
            ALL PRODUCTS
          </button>
          {activeCategories.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              className={`shrink-0 text-xs font-bold tracking-wider px-5 py-4 border-b-2 transition-all whitespace-nowrap ${
                category === c.key ? 'border-primary text-primary' : 'border-transparent text-white/30 hover:text-white/60'
              }`}
            >
              {c.label}
            </button>
          ))}
          <button
            onClick={() => setShowFilters((s) => !s)}
            className={`shrink-0 ml-auto inline-flex items-center gap-1.5 text-xs font-bold tracking-wider px-4 py-2 my-2 border transition-colors ${showFilters || activeFilterCount ? 'border-primary/40 text-primary bg-primary/5' : 'border-white/10 text-white/50 hover:text-white'}`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> FILTERS{activeFilterCount ? ` (${activeFilterCount})` : ''}
          </button>
          <span className="text-white/20 text-xs font-mono shrink-0 pl-4">
            {filtered.length} ITEM{filtered.length !== 1 ? 'S' : ''}
          </span>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="border-b border-white/5 bg-[#111115]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5">
            <ShopFilters
              filters={filters}
              onChange={setFilters}
              brands={availableBrands}
              priceBounds={priceBounds}
            />
          </div>
        </div>
      )}

      {/* Grid */}
      <section className="px-6 lg:px-10 py-12 max-w-7xl mx-auto">
        {productsLoading ? (
          <div className="flex items-center justify-center py-40">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-40">
            <p className="text-white/30 text-sm">
              {search ? `No products match "${search}".` : 'No products found.'}
            </p>
            {search && (
              <button onClick={() => setSearch('')} className="mt-3 text-primary text-xs font-bold hover:underline">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Recently viewed */}
      <RecentlyViewed />

      {/* Testimonials */}
      <Testimonials />

      {/* Compare tray */}
      <CompareBar />
    </div>
  );
}